import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { Header } from "../../components/Header/Header";
import { Footer } from "../../components/Footer/Footer";
import { useAuth } from "../../../context/AuthContext";
import { db } from "../../../service/firebase";
import {
  fetchPendingTurmaInvites,
  respondToTurmaInvite,
  joinTurmaViaLink,
} from "../../../service/turmaService";
import "./Turmas.css";

const TURMA_STATUS_LABELS = {
  0: "Inativa",
  1: "Ativa",
  2: "Encerrada",
};

const Turmas = () => {
  const { user } = useAuth();
  const [turmas, setTurmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [invites, setInvites] = useState([]);
  const [inviteMessage, setInviteMessage] = useState("");
  const [joinLink, setJoinLink] = useState("");
  const [joinPassword, setJoinPassword] = useState("");
  const [joinError, setJoinError] = useState("");
  const [joinSuccess, setJoinSuccess] = useState("");
  const [joining, setJoining] = useState(false);

  const fetchTurmaList = async (userId) => {
    const membershipsQuery = query(
      collection(db, "jogador_turma"),
      where("id_usuario", "==", userId),
      where("status", "==", 1)
    );

    const membershipsSnapshot = await getDocs(membershipsQuery);
    const turmaIds = membershipsSnapshot.docs.map((docSnapshot) => ({
      id: docSnapshot.data().id_turma,
      admin: docSnapshot.data().admin,
    }));

    if (turmaIds.length === 0) {
      return [];
    }

    const turmaPromises = turmaIds.map(async ({ id, admin }) => {
      const turmaDoc = await getDoc(doc(db, "turmas", id));
      if (!turmaDoc.exists()) {
        return null;
      }

      return { id, admin, ...turmaDoc.data() };
    });

    const turmasResultado = (await Promise.all(turmaPromises)).filter(Boolean);
    return turmasResultado;
  };

  useEffect(() => {
    const fetchTurmas = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const resultado = await fetchTurmaList(user.uid);
        setTurmas(resultado);
      } catch (fetchError) {
        console.error("Erro ao carregar turmas", fetchError);
        setError("Não foi possível carregar suas turmas agora.");
      } finally {
        setLoading(false);
      }
    };

    fetchTurmas();
  }, [user]);

  useEffect(() => {
    const fetchInvites = async () => {
      if (!user) return;
      try {
        const pendingInvites = await fetchPendingTurmaInvites(user.uid);
        setInvites(pendingInvites);
      } catch (inviteError) {
        console.error("Erro ao carregar convites", inviteError);
      }
    };
    fetchInvites();
  }, [user]);

  const handleInviteResponse = async (inviteId, accept) => {
    setInviteMessage("");
    try {
      await respondToTurmaInvite({ inviteId, accept, userId: user.uid });
      setInvites((current) => current.filter((invite) => invite.id !== inviteId));
      if (accept) {
        setInviteMessage("Convite aceito com sucesso. Veja suas turmas abaixo.");
        setLoading(true);
        try {
          const data = await fetchTurmaList(user.uid);
          setTurmas(data);
        } catch (listError) {
          console.error("Erro ao atualizar lista de turmas", listError);
        } finally {
          setLoading(false);
        }
      }
    } catch (err) {
      console.error("Erro ao responder convite", err);
      setError("Não foi possível atualizar o convite no momento.");
    }
  };

  const extractTurmaIdFromLink = (value) => {
    if (!value) return null;
    const trimmed = value.trim();
    const linkMatch = trimmed.match(/convite\/turma\/([\w-]+)/i);
    if (linkMatch) {
      return linkMatch[1];
    }
    if (/^[\w-]+$/.test(trimmed)) {
      return trimmed;
    }
    return null;
  };

  const handleJoinViaLink = async () => {
    if (!user) return;
    setJoinError("");
    setJoinSuccess("");
    const turmaId = extractTurmaIdFromLink(joinLink);
    if (!turmaId) {
      setJoinError("Cole um link ou código válido.");
      return;
    }
    setJoining(true);
    try {
      await joinTurmaViaLink({ turmaId, userId: user.uid, senha: joinPassword });
      setJoinSuccess("Você entrou na turma! Atualizamos a lista abaixo.");
      setJoinLink("");
      setJoinPassword("");
      const data = await fetchTurmaList(user.uid);
      setTurmas(data);
    } catch (err) {
      setJoinError(err.message || "Não foi possível entrar na turma.");
    } finally {
      setJoining(false);
    }
  };

  return (
    <>
      <Header />
      <div className="body">
        <div className="center">
          <div className="body-content card turmas-card">
            <div className="topo-page">
              <strong># Turmas</strong>
            </div>
            {inviteMessage && <p className="turmas-success">{inviteMessage}</p>}
            {error && (
              <p className="turmas-error" role="alert">
                {error}
              </p>
            )}
            {invites.length > 0 && (
              <div className="turmas-invites">
                <h3>Convites pendentes</h3>
                {invites.map((invite) => (
                  <div key={invite.id} className="turma-invite-item">
                    <div>
                      <strong>{invite.turma_nome}</strong>
                      <p>Você foi convidado para esta turma.</p>
                    </div>
                    <div className="turma-invite-actions">
                      <button
                        type="button"
                        className="btn-default"
                        onClick={() => handleInviteResponse(invite.id, true)}
                      >
                        Aceitar
                      </button>
                      <button
                        type="button"
                        className="btn-outline"
                        onClick={() => handleInviteResponse(invite.id, false)}
                      >
                        Recusar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {loading ? (
              <div className="turmas-placeholder">
                <span>Carregando suas turmas...</span>
              </div>
            ) : turmas.length === 0 ? (
              <p className="turmas-description">
                Você ainda não está em nenhuma turma. Crie uma nova turma ou
                entre em uma turma através de um convite.
              </p>
            ) : (
              <div className="turmas-list">
                {turmas.map((turma) => (
                  <Link
                    key={turma.id}
                    to={`/turmas/${turma.id}`}
                    className="turma-item"
                  >
                    <div className="turma-item-info">
                      <div>
                        <strong>| {turma.nome}</strong>
                      </div>
                      <div>
                        <small>
                          • {turma.dias_semana?.join(", ") || "Não informado"}
                        </small>
                      </div>
                      <div>
                        <small>• Local: {turma.local}</small> <br />
                      </div>
                      <div className="turma-status-row">
                        <span
                          className={`turma-status-badge status-${
                            turma.status ?? 1
                          }`}
                        >
                          {TURMA_STATUS_LABELS[turma.status ?? 1]}
                        </span>
                        {turma.admin && <span className="turma-chip">Admin</span>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
            <div className="turmas-join-link">
              <h3>Entrar com link</h3>
              <p>Cole o link recebido para acessar diretamente uma turma.</p>
              <div className="turmas-join-link-form">
                <input
                  type="text"
                  placeholder="https://app.../convite/turma/abc123"
                  value={joinLink}
                  onChange={(event) => setJoinLink(event.target.value)}
                />
                <input
                  type="password"
                  placeholder="Senha da turma (se necessário)"
                  value={joinPassword}
                  onChange={(event) => setJoinPassword(event.target.value)}
                />
                <button
                  type="button"
                  className="btn-default"
                  onClick={handleJoinViaLink}
                  disabled={joining}
                >
                  {joining ? "Entrando..." : "Entrar"}
                </button>
              </div>
              {joinError && (
                <p className="turmas-error" role="alert">
                  {joinError}
                </p>
              )}
              {joinSuccess && (
                <p className="turmas-success">{joinSuccess}</p>
              )}
            </div>
            <div className="turmas-actions">
              <Link to="/turmas/nova" className="btn-default">
                Criar turma
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Turmas;
